<template>

    <div class="tc-page">

        <!-- 1. En-tête + filtres -->
        <div class="ds-page-head">
            <div>
                <h1 class="ds-page-head__title">Trésorerie et caisses</h1>
                <p class="ds-page-head__sub">
                    {{ libellePeriode }}
                    <template v-if="derniereMaj"> · données de {{ derniereMaj }}</template>
                </p>
            </div>
            <div class="tc-filters">
                <div class="tc-seg" role="group" aria-label="Période">
                    <button v-for="p in presets" :key="p.code" type="button"
                            :class="{ 'tc-seg--on': preset === p.code }"
                            @click="choisirPreset(p.code)">{{ p.label }}</button>
                </div>
                <template v-if="preset === 'perso'">
                    <input type="date" class="tc-input" v-model="dateDebut" :max="dateFin" @change="charger">
                    <input type="date" class="tc-input" v-model="dateFin" :min="dateDebut" @change="charger">
                </template>
                <select class="tc-input" v-model="pidcaisse" @change="charger" aria-label="Caisse">
                    <option value="">Toutes les caisses</option>
                    <option v-for="c in caisses" :key="c.pid" :value="c.pid">{{ c.name }}</option>
                </select>
                <button type="button" class="tc-ibtn" title="Actualiser" :disabled="chargement" @click="charger(true)">
                    <i class="ri-refresh-line" :class="{ 'tc-spin': chargement }"></i>
                </button>
            </div>
        </div>

        <!-- Données servies par le cache 5 min -->
        <bandeaucachetb v-if="depuisCache" quoi="les chiffres de trésorerie et de caisse" verbe="sont gardés"
                        manque="Les encaissements, dépenses et mouvements de caisse faits depuis n'y figurent pas encore."
                        :heure="heureCache" @actualiser="charger(true)"></bandeaucachetb>

        <!-- Erreur -->
        <div v-if="erreur" class="tc-alert tc-alert--bad">
            <i class="ri-error-warning-line"></i>
            <span>Impossible de charger le tableau de bord de caisse.</span>
            <a href="javascript:void(0)" @click="charger(true)">Réessayer</a>
        </div>

        <!-- 2. Alertes -->
        <div v-if="alertes.length" class="tc-alerts">
            <div v-for="(a, i) in alertes" :key="i" class="tc-alert" :class="'tc-alert--' + a.niveau">
                <i :class="a.icone"></i>
                <span v-html="a.texte"></span>
                <a v-if="a.pid" href="javascript:void(0)" @click="btnmodalanalysefinancier(a.pid)">Analyse de caisse <i class="ri-bar-chart-2-line"></i></a>
            </div>
        </div>

        <!-- 3. KPI -->
        <div class="tc-kpis">
            <template v-if="charge">
                <div class="tc-kpi tc-kpi--main">
                    <div class="tc-kpi__lbl">Trésorerie disponible <i class="ri-safe-2-line"></i></div>
                    <div class="tc-kpi__val">{{ formatMoney(totalTresorerie) }} <small>F CFA</small></div>
                    <div class="tc-kpi__split">
                        <span><i class="ri-wallet-3-line"></i> Caisses {{ formatMoney(totalCaisses) }}</span>
                        <span><i class="ri-bank-line"></i> Banques {{ formatMoney(totalBanques) }}</span>
                    </div>
                </div>
                <div class="tc-kpi">
                    <div class="tc-kpi__lbl">Recettes de la période <i class="ri-arrow-down-circle-line tc-ico-rec"></i></div>
                    <div class="tc-kpi__val">{{ formatMoney(kpi.recettes) }} <small>F</small></div>
                    <div class="tc-kpi__sub">
                        <span v-if="variation(kpi.recettes, precedent.recettes) !== null"
                              :class="variation(kpi.recettes, precedent.recettes) >= 0 ? 'tc-up' : 'tc-down'">
                            {{ fleche(kpi.recettes, precedent.recettes) }}
                        </span>
                        <span v-else class="tc-faint">pas de période précédente</span>
                        · dont ventes {{ formatMoney(kpi.ventes) }}
                    </div>
                </div>
                <div class="tc-kpi">
                    <div class="tc-kpi__lbl">Dépenses de la période <i class="ri-arrow-up-circle-line tc-ico-dep"></i></div>
                    <div class="tc-kpi__val">{{ formatMoney(kpi.depenses) }} <small>F</small></div>
                    <div class="tc-kpi__sub">
                        <span v-if="variation(kpi.depenses, precedent.depenses) !== null"
                              :class="variation(kpi.depenses, precedent.depenses) > 0 ? 'tc-down' : 'tc-up'">
                            {{ fleche(kpi.depenses, precedent.depenses) }}
                        </span>
                        <span v-else class="tc-faint">pas de période précédente</span>
                        · solde net {{ formatMoney(kpi.recettes - kpi.depenses) }}
                    </div>
                </div>
                <div class="tc-kpi">
                    <div class="tc-kpi__lbl">Caisses ouvertes <i class="ri-door-open-line tc-ico-ok"></i></div>
                    <div class="tc-kpi__val">{{ nbOuvertes }} <small>/ {{ caisses.length }}</small></div>
                    <div class="tc-kpi__sub">
                        Écart cumulé :
                        <span :class="kpi.ecart < 0 ? 'tc-down' : (kpi.ecart > 0 ? 'tc-up' : 'tc-faint')">{{ formatSigne(kpi.ecart) }}</span>
                        <template v-if="kpi.nbEcarts"> ({{ kpi.nbEcarts }} fermeture{{ kpi.nbEcarts > 1 ? 's' : '' }})</template>
                    </div>
                </div>
            </template>
            <template v-else>
                <div v-for="n in 4" :key="n" class="tc-kpi">
                    <div class="tc-skel" style="width:45%"></div>
                    <div class="tc-skel tc-skel--lg" style="width:70%"></div>
                    <div class="tc-skel" style="width:85%"></div>
                </div>
            </template>
        </div>

        <!-- 4. Graphique + répartition -->
        <div class="tc-grid2">
            <div class="tc-card">
                <div class="tc-card__head">
                    <h2>Recettes et dépenses <span>· par {{ granularite === 'mois' ? 'mois' : 'jour' }}, {{ pidcaisse ? nomCaisse(pidcaisse) : 'toutes caisses' }}</span></h2>
                </div>
                <div class="tc-card__body">
                    <template v-if="charge">
                        <apexcharts v-if="aDesMouvements" type="line" height="290" :options="chartOptions" :series="chartSeries"></apexcharts>
                        <div v-else class="cf-empty">
                            <i class="ri-inbox-2-line"></i>
                            Aucune journée de caisse sur cette période.<br>
                            <span class="tc-faint">Élargissez la période ou choisissez une autre caisse.</span>
                        </div>
                    </template>
                    <div v-else class="tc-skel tc-skel--chart"></div>
                </div>
            </div>

            <div class="tc-card">
                <div class="tc-card__head"><h2>Répartition <span>· période</span></h2></div>
                <div class="tc-card__body">
                    <template v-if="charge">
                        <p class="tc-rep__title">Recettes par caisse</p>
                        <div v-if="!parcaisse.length" class="tc-faint tc-small">Aucune recette.</div>
                        <div v-for="c in parcaisse" :key="c.pid" class="tc-rep">
                            <div class="tc-rep__top"><span>{{ c.name }}</span><b>{{ formatMoney(c.recettes) }}</b></div>
                            <div class="tc-bar"><i :style="{ width: part(c.recettes, kpi.recettes) + '%' }"></i></div>
                        </div>

                        <p class="tc-rep__title tc-rep__title--sep">Dépenses par nature</p>
                        <div v-if="!kpi.depenses" class="tc-faint tc-small">Aucune dépense.</div>
                        <template v-else>
                            <div v-for="d in natures" :key="d.label" class="tc-rep">
                                <div class="tc-rep__top"><span>{{ d.label }}</span><b>{{ formatMoney(d.valeur) }}</b></div>
                                <div class="tc-bar"><i class="tc-bar--dep" :style="{ width: part(d.valeur, kpi.depenses) + '%' }"></i></div>
                            </div>
                        </template>
                    </template>
                    <template v-else>
                        <div v-for="n in 4" :key="n" class="tc-skel"></div>
                    </template>
                </div>
            </div>
        </div>

        <!-- 5. Caisses + banques -->
        <div class="tc-grid2">
            <div class="tc-card">
                <div class="tc-card__head">
                    <h2>Caisses <span>· journée en cours</span></h2>
                </div>

                <template v-if="charge">
                    <div v-if="!caisses.length" class="cf-empty"><i class="ri-wallet-3-line"></i>Aucune caisse définie.</div>

                    <!-- tableau (écran large) -->
                    <div v-else class="tc-table-wrap tc-only-desktop">
                        <table class="w-full">
                            <thead>
                            <tr>
                                <th class="th">Caisse</th>
                                <th class="th">Statut</th>
                                <th class="th th--right">Ouverture</th>
                                <th class="th th--right">Recettes</th>
                                <th class="th th--right">Dépenses</th>
                                <th class="th th--right">Solde</th>
                                <th class="th"></th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="c in caisses" :key="c.pid" class="table-row">
                                <td class="td">
                                    <div class="tc-cname">{{ c.name }}</div>
                                    <div class="tc-cmeta" v-if="c.ouverte">{{ c.ouvripar || '-' }} · depuis le {{ formatDate(c.ouvrile) }}</div>
                                </td>
                                <td class="td">
                                    <span class="cf-badge" :class="badge(c).cls">{{ badge(c).txt }}</span>
                                </td>
                                <td class="td td--right">{{ c.ouverte ? formatMoney(c.ouverture) : '-' }}</td>
                                <td class="td td--right">{{ c.ouverte ? formatMoney(c.recettes) : '-' }}</td>
                                <td class="td td--right">{{ c.ouverte ? formatMoney(c.depenses) : '-' }}</td>
                                <td class="td td--right tc-strong">{{ formatMoney(c.solde) }}</td>
                                <td class="td td--right tc-nowrap">
                                    <button type="button" class="cf-iconbtn" title="Analyse de caisse" @click="btnmodalanalysefinancier(c.pid)"><i class="ri-bar-chart-2-line"></i></button>
                                </td>
                            </tr>
                            </tbody>
                            <tfoot>
                            <tr class="tc-foot">
                                <td class="td" colspan="3">Total</td>
                                <td class="td td--right">{{ formatMoney(totalOuvertes('recettes')) }}</td>
                                <td class="td td--right">{{ formatMoney(totalOuvertes('depenses')) }}</td>
                                <td class="td td--right">{{ formatMoney(totalCaisses) }}</td>
                                <td class="td"></td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>

                    <!-- cartes (mobile) -->
                    <div v-if="caisses.length" class="tc-mcards tc-only-mobile">
                        <div v-for="c in caisses" :key="c.pid" class="tc-mcard">
                            <div class="tc-mcard__r1">
                                <div>
                                    <div class="tc-cname">{{ c.name }}</div>
                                    <div class="tc-cmeta" v-if="c.ouverte">{{ c.ouvripar || '-' }} · {{ formatDate(c.ouvrile) }}</div>
                                </div>
                                <span class="cf-badge" :class="badge(c).cls">{{ badge(c).txt }}</span>
                            </div>
                            <div class="tc-mcard__r2">
                                <div>Recettes<b>{{ c.ouverte ? formatMoney(c.recettes) : '-' }}</b></div>
                                <div>Dépenses<b>{{ c.ouverte ? formatMoney(c.depenses) : '-' }}</b></div>
                                <div>Solde<b>{{ formatMoney(c.solde) }}</b></div>
                            </div>
                            <div class="tc-mcard__r3">
                                <button type="button" class="cf-iconbtn" title="Analyse de caisse" @click="btnmodalanalysefinancier(c.pid)"><i class="ri-bar-chart-2-line"></i></button>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-else class="tc-card__body">
                    <div v-for="n in 4" :key="n" class="tc-skel"></div>
                </div>
            </div>

            <div class="tc-card">
                <div class="tc-card__head">
                    <h2>Banques</h2>
                    <span class="tc-strong" v-if="charge">{{ formatMoney(totalBanques) }} F</span>
                </div>
                <div class="tc-card__body">
                    <template v-if="charge">
                        <div v-if="!banques.length" class="cf-empty"><i class="ri-bank-line"></i>Aucune banque définie.</div>
                        <div v-for="b in banques" :key="b.pid" class="tc-bank">
                            <span><i class="ri-bank-line tc-faint"></i> {{ b.name }}</span>
                            <b :class="{ 'tc-down': b.solde < 0 }">{{ formatMoney(b.solde) }}</b>
                        </div>
                    </template>
                    <template v-else>
                        <div v-for="n in 3" :key="n" class="tc-skel"></div>
                    </template>
                </div>

                <template v-if="charge && ecarts.length">
                    <div class="tc-card__head tc-card__head--sep"><h2>Écarts à la fermeture <span>· période</span></h2></div>
                    <div class="tc-card__body">
                        <div v-for="(e, i) in ecarts" :key="i" class="tc-bank">
                            <span>{{ formatDate(e.j) }} · {{ e.name }}</span>
                            <b :class="e.ecart < 0 ? 'tc-down' : 'tc-up'">{{ formatSigne(e.ecart) }}</b>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <modalpointdelacaisse ref="modalpointdelacaisse"></modalpointdelacaisse>
        <modalrapportdecaisse6 ref="modalrapportdecaisse6"></modalrapportdecaisse6>

    </div>

</template>

<script>

    import {currencyFormatDE, randomString} from "@/allfunction";
    import * as JwtService from "@/common/jwt.service";
    const axios =require('axios');
    import VueApexCharts from "vue3-apexcharts";
    import modalpointdelacaisse from "@/components/views/pages/caisse/rapport/modal_pointdelacaisse";
    import { avecCacheTB } from "@/service/cacheTableauDeBord";
    import bandeaucachetb from "@/components/views/pages/tableaudebord/bandeaucachetb";
    import modalrapportdecaisse6
      from "@/components/views/pages/caisse/facturation/5/rapport/modal_rapportdecaisse_6v2.vue";

    const COULEUR_RECETTES = '#7c5ae0';
    const COULEUR_DEPENSES = '#f59e0b';
    const COULEUR_NET = '#1e293b';
    const MOIS = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];

    /* Date locale AAAA-MM-JJ (pas toISOString, qui passe en UTC). */
    const isoLocal = (d) => {
        const p = (n) => String(n).padStart(2, '0');
        return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
    };

    export default {
        /* eslint-disable */
        name: "caisse",
        components: {
            bandeaucachetb,
            apexcharts: VueApexCharts,
            modalpointdelacaisse,modalrapportdecaisse6,
        },
        data() {
            return {
                depuisCache: false,
                heureCache: '',
                initcomponnent:false,
                chargement:false,
                charge:false,
                erreur:false,
                derniereMaj:'',

                presets:[
                    {code:'7j', label:'7 jours'},
                    {code:'mois', label:'Ce mois'},
                    {code:'moisprec', label:'Mois dernier'},
                    {code:'3m', label:'3 mois'},
                    {code:'12m', label:'12 mois'},
                    {code:'perso', label:'Dates'},
                ],
                preset:'7j',
                dateDebut:'',
                dateFin:'',
                pidcaisse:'',

                periode:{},
                granularite:'jour',
                kpi:{recettes:0, ventes:0, depenses:0, depense:0, reglfrs:0, transfert:0, ecart:0, nbEcarts:0, nbJournees:0},
                precedent:{recettes:0, depenses:0},
                serie:[],
                parcaisse:[],
                ecarts:[],
                caisses:[],
                banques:[],
            };
        },
        computed: {
            totalCaisses(){
                return this.caisses.reduce((s, c) => s + Number(c.solde || 0), 0);
            },
            totalBanques(){
                return this.banques.reduce((s, b) => s + Number(b.solde || 0), 0);
            },
            totalTresorerie(){
                return this.totalCaisses + this.totalBanques;
            },
            nbOuvertes(){
                return this.caisses.filter(c => c.ouverte).length;
            },
            aDesMouvements(){
                return this.serie.some(p => p.recettes || p.depenses);
            },
            natures(){
                return [
                    {label:'Règlements fournisseurs', valeur:this.kpi.reglfrs},
                    {label:'Dépenses courantes', valeur:this.kpi.depense},
                    {label:'Transferts vers dépôt', valeur:this.kpi.transfert},
                ].filter(n => n.valeur);
            },
            alertes(){
                const a = [];
                for (const c of this.caisses) {
                    if (c.ouverte && c.joursOuverte >= 1) {
                        a.push({
                            niveau: c.joursOuverte >= 3 ? 'bad' : 'warn',
                            icone: 'ri-alarm-warning-line',
                            texte: '<b>' + this.echapper(c.name) + '</b> est ouverte depuis le ' + this.formatDate(c.ouvrile)
                                + ' (' + c.joursOuverte + ' jour' + (c.joursOuverte > 1 ? 's' : '') + ')'
                                + (c.ouvripar ? ' par ' + this.echapper(c.ouvripar) : '') + '.',
                            pid: c.pid,
                        });
                    }
                    if (c.solde < 0) {
                        a.push({
                            niveau: 'bad', icone: 'ri-error-warning-line',
                            texte: '<b>' + this.echapper(c.name) + '</b> a un solde négatif : ' + this.formatMoney(c.solde) + ' F.',
                            pid: c.pid,
                        });
                    }
                }
                if (this.kpi.nbEcarts) {
                    a.push({
                        niveau: 'warn', icone: 'ri-scales-3-line',
                        texte: this.kpi.nbEcarts + ' écart' + (this.kpi.nbEcarts > 1 ? 's' : '') + ' de caisse à la fermeture sur la période, cumul <b>'
                            + this.formatSigne(this.kpi.ecart) + ' F</b> (détail sous les banques).',
                        pid: '',
                    });
                }
                return a;
            },
            libellePeriode(){
                if (!this.periode.debut) return 'Chargement de la période';
                if (this.periode.debut === this.periode.fin) return 'Journée du ' + this.formatDate(this.periode.debut);
                return 'Du ' + this.formatDate(this.periode.debut) + ' au ' + this.formatDate(this.periode.fin)
                    + ' (' + this.periode.nbjours + ' jours)';
            },
            chartSeries(){
                return [
                    {name:'Recettes', type:'column', data:this.serie.map(p => p.recettes)},
                    {name:'Dépenses', type:'column', data:this.serie.map(p => p.depenses)},
                    {name:'Solde net', type:'line', data:this.serie.map(p => Math.round((p.recettes - p.depenses) * 100) / 100)},
                ];
            },
            chartOptions(){
                const t = this;
                return {
                    chart:{type:'line', toolbar:{show:false}, zoom:{enabled:false}, fontFamily:'inherit', animations:{enabled:false}},
                    colors:[COULEUR_RECETTES, COULEUR_DEPENSES, COULEUR_NET],
                    plotOptions:{bar:{columnWidth:'60%', borderRadius:2}},
                    stroke:{width:[0, 0, 2], curve:'straight'},
                    dataLabels:{enabled:false},
                    legend:{position:'top', horizontalAlign:'right', fontSize:'12px'},
                    grid:{borderColor:'#e2e8f0', strokeDashArray:3},
                    xaxis:{
                        categories:this.serie.map(p => t.libelleAxe(p.cle)),
                        labels:{rotate:-45, hideOverlappingLabels:true, style:{colors:'#94a3b8', fontSize:'11px'}},
                        axisTicks:{show:false},
                        tickAmount: this.serie.length > 16 ? 10 : undefined,
                    },
                    yaxis:{labels:{style:{colors:'#94a3b8', fontSize:'11px'}, formatter:(v) => t.abrege(v)}},
                    tooltip:{shared:true, intersect:false, y:{formatter:(v) => t.formatMoney(v) + ' F'}},
                };
            },
        },
        methods: {

            btnmodalpointdelacaisse(numpidcaisse){
                let datedouverture="";
                this.$refs.modalpointdelacaisse.show(numpidcaisse,datedouverture);
            },

            btnmodalanalysefinancier(numpidcaisse){
                let datedouverture="";
                this.$refs.modalrapportdecaisse6.show("ANALYSE DE CAISSE", numpidcaisse, datedouverture, 'FVC');
            },

            formatMoney(value){
                return currencyFormatDE(value)
            },

            formatSigne(value){
                const v = Number(value || 0);
                return (v > 0 ? '+' : '') + currencyFormatDE(v);
            },

            formatDate(j){
                if (!j) return '';
                const s = String(j).slice(0, 10).split('-');
                return s.length === 3 ? s[2] + '/' + s[1] + '/' + s[0] : j;
            },

            libelleAxe(cle){
                const s = String(cle).split('-');
                if (s.length === 2) return MOIS[parseInt(s[1], 10) - 1] + ' ' + s[0].slice(2);
                return s[2] + '/' + s[1];
            },

            abrege(v){
                const a = Math.abs(v);
                if (a >= 1e6) return (v / 1e6).toFixed(1).replace('.0', '') + ' M';
                if (a >= 1e3) return Math.round(v / 1e3) + ' k';
                return Math.round(v);
            },

            echapper(s){
                return String(s || '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
            },

            variation(actuel, prec){
                if (!prec) return null;
                return (actuel - prec) / Math.abs(prec) * 100;
            },

            fleche(actuel, prec){
                const v = this.variation(actuel, prec);
                if (v === null) return '';
                return (v >= 0 ? '▲ ' : '▼ ') + Math.abs(Math.round(v)) + ' % vs période précédente';
            },

            part(v, total){
                if (!total) return 0;
                return Math.max(2, Math.min(100, Math.round(v / total * 100)));
            },

            nomCaisse(pid){
                const c = this.caisses.find(x => x.pid === pid);
                return c ? c.name : '';
            },

            totalOuvertes(champ){
                return this.caisses.filter(c => c.ouverte).reduce((s, c) => s + Number(c[champ] || 0), 0);
            },

            badge(c){
                if (!c.ouverte) return {cls:'tc-badge--off', txt:'Fermée'};
                if (c.joursOuverte >= 1) return {cls:'tc-badge--bad', txt:'Ouverte ' + c.joursOuverte + ' j'};
                return {cls:'tc-badge--ok', txt:'Ouverte'};
            },

            choisirPreset(code){
                this.preset = code;
                const now = new Date();
                let d1 = new Date(now), d2 = new Date(now);
                if (code === '7j') d1.setDate(d1.getDate() - 6);
                if (code === 'mois') d1 = new Date(now.getFullYear(), now.getMonth(), 1);
                if (code === 'moisprec') {
                    d1 = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    d2 = new Date(now.getFullYear(), now.getMonth(), 0);
                }
                if (code === '3m') d1 = new Date(now.getFullYear(), now.getMonth() - 2, 1);
                if (code === '12m') d1 = new Date(now.getFullYear(), now.getMonth() - 11, 1);
                if (code === 'perso') {
                    if (!this.dateDebut || !this.dateFin) { this.dateDebut = isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)); this.dateFin = isoLocal(now); }
                    this.charger();
                    return;
                }
                this.dateDebut = isoLocal(d1);
                this.dateFin = isoLocal(d2);
                this.charger();
            },

            async charger(force) {

                if (this.chargement) return;
                const t = this;
                this.chargement = true;
                this.erreur = false;

                let url=process.env.VUE_APP_API_URL+'/egl745874tb771714dfe/dashboard/caisse/'+this.$store.state.appsociete+'/'+randomString();

                const params = { dateDebut:this.dateDebut, dateFin:this.dateFin, pidcaisse:this.pidcaisse };

                try {

                    const r = await avecCacheTB(this.$store.state.appsociete, 'caisse', params, async () => {
                        let res=await axios.post(url,params,{
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + JwtService.getToken(),
                                'csrftoken': this.$store.state.appsociete
                            } ,withCredentials: true // Permet l'envoi de cookies et autres informations d'authentification
                        });
                        return res.data.success ? res.data.m : null;
                    }, { force: force === true });

                    if (r.data){
                        const m = r.data;
                        t.periode = m.periode;
                        t.granularite = m.periode.granularite;
                        t.dateDebut = m.periode.debut;
                        t.dateFin = m.periode.fin;
                        t.kpi = m.kpi;
                        t.precedent = m.precedent;
                        t.serie = m.serie;
                        t.parcaisse = m.parcaisse;
                        t.ecarts = m.ecarts;
                        t.caisses = m.caisses;
                        t.banques = m.banques;
                        t.charge = true;
                        t.depuisCache = r.cache;
                        t.heureCache = String(new Date(r.t).getHours()).padStart(2, '0') + ':' + String(new Date(r.t).getMinutes()).padStart(2, '0');
                        const h = new Date(r.t);
                        t.derniereMaj = String(h.getHours()).padStart(2, '0') + ':' + String(h.getMinutes()).padStart(2, '0');
                    }
                    else{
                        t.erreur = true;
                    }

                } catch (e) {
                    t.erreur = true;
                    toastr['error']( "Une erreur est subvenue. <br/> veuillez actualiser la page.", "<b></b>");
                } finally {
                    t.chargement = false;
                }

            },

            loadinitcomponnent(){

                if (this.initcomponnent===false){
                    this.choisirPreset('7j');
                    this.initcomponnent=true;
                }

            },

        }
    }
</script>

<style scoped>

.tc-page { padding-bottom: 1rem; }

/* filtres */
.tc-filters { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.tc-seg { display: inline-flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 3px; gap: 2px; }
.tc-seg button { border: 0; background: transparent; font-size: 0.8rem; font-weight: 500; padding: 0.3rem 0.7rem; border-radius: var(--cf-radius-xs); color: var(--cf-muted); cursor: pointer; white-space: nowrap; }
.tc-seg button:hover { color: rgb(var(--primary)); }
.tc-seg button.tc-seg--on { background: rgb(var(--primary)); color: #fff; }
.tc-input { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.38rem 0.6rem; font-size: 0.8rem; color: var(--cf-ink); max-width: 100%; }
.tc-ibtn { width: 2.1rem; height: 2.1rem; display: inline-flex; align-items: center; justify-content: center; background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); color: var(--cf-muted); cursor: pointer; }
.tc-ibtn:hover { color: rgb(var(--primary)); }
.tc-spin { display: inline-block; animation: tc-rot 0.9s linear infinite; }
@keyframes tc-rot { to { transform: rotate(360deg); } }

/* alertes */
.tc-alerts { display: grid; gap: 0.5rem; margin-bottom: 1rem; }
.tc-alert { display: flex; gap: 0.6rem; align-items: center; padding: 0.6rem 0.9rem; border-radius: var(--cf-radius-sm); font-size: 0.82rem; margin-bottom: 0.5rem; }
.tc-alerts .tc-alert { margin-bottom: 0; }
.tc-alert i { font-size: 1.1rem; }
.tc-alert a { margin-left: auto; font-weight: 600; color: inherit; white-space: nowrap; }
.tc-alert--bad { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
.tc-alert--warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }

/* KPI */
.tc-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
@media (min-width: 1024px) { .tc-kpis { grid-template-columns: 1.3fr 1fr 1fr 1fr; } }
.tc-kpi { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); padding: 0.9rem 1rem; min-width: 0; }
.tc-kpi__lbl { display: flex; justify-content: space-between; align-items: center; color: var(--cf-muted); font-size: 0.78rem; font-weight: 500; }
.tc-kpi__lbl i { width: 1.9rem; height: 1.9rem; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 1rem; }
.tc-kpi__val { font-size: 1.45rem; font-weight: 800; color: var(--cf-ink-strong); margin: 0.35rem 0 0.1rem; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.tc-kpi__val small { font-size: 0.78rem; font-weight: 500; color: var(--cf-muted); }
.tc-kpi__sub { font-size: 0.72rem; color: var(--cf-muted); }
.tc-kpi__split { display: flex; flex-wrap: wrap; gap: 0.25rem 0.9rem; font-size: 0.72rem; margin-top: 0.3rem; }
.tc-kpi--main { background: linear-gradient(135deg, rgb(var(--primary)) 0%, #6d4bd0 100%); border: 0; color: #fff; }
.tc-kpi--main .tc-kpi__lbl, .tc-kpi--main .tc-kpi__split { color: rgb(255 255 255 / 0.85); }
.tc-kpi--main .tc-kpi__lbl i { background: rgb(255 255 255 / 0.18); }
.tc-kpi--main .tc-kpi__val, .tc-kpi--main .tc-kpi__val small { color: #fff; }
.tc-ico-rec { background: #f1ecfd; color: #7c5ae0; }
.tc-ico-dep { background: #fff7e6; color: #d97706; }
.tc-ico-ok { background: #ecfdf5; color: #059669; }
.tc-up { color: #059669; font-weight: 600; }
.tc-down { color: #dc2626; font-weight: 600; }
.tc-faint { color: var(--cf-faint); }
.tc-small { font-size: 0.78rem; }
.tc-strong { font-weight: 700; color: var(--cf-ink-strong); }
.tc-nowrap { white-space: nowrap; }

/* cartes */
.tc-grid2 { display: grid; gap: 1rem; margin-bottom: 1rem; }
@media (min-width: 1024px) { .tc-grid2 { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); } }
.tc-card { background: #fff; border: 1px solid var(--cf-border); border-radius: var(--cf-radius); box-shadow: var(--cf-shadow-sm); min-width: 0; }
.tc-card__head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap; padding: 0.75rem 1rem; border-bottom: 1px solid var(--cf-border); }
.tc-card__head--sep { border-top: 1px solid var(--cf-border); }
.tc-card__head h2 { margin: 0; font-size: 0.9rem; font-weight: 700; color: var(--cf-ink-strong); }
.tc-card__head h2 span { font-size: 0.75rem; font-weight: 400; color: var(--cf-muted); }
.tc-card__body { padding: 0.85rem 1rem; }

/* répartition */
.tc-rep { margin-bottom: 0.65rem; }
.tc-rep__title { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--cf-faint); margin: 0 0 0.5rem; }
.tc-rep__title--sep { border-top: 1px solid var(--cf-border); padding-top: 0.75rem; margin-top: 0.75rem; }
.tc-rep__top { display: flex; justify-content: space-between; gap: 0.5rem; font-size: 0.8rem; margin-bottom: 0.25rem; }
.tc-rep__top b { font-variant-numeric: tabular-nums; }
.tc-bar { height: 7px; background: var(--cf-surface-alt); border-radius: 99px; overflow: hidden; }
.tc-bar i { display: block; height: 100%; border-radius: 99px; background: rgb(var(--primary)); }
.tc-bar i.tc-bar--dep { background: #f59e0b; }

/* tableau */
.tc-table-wrap { overflow-x: auto; }
.th { padding: 0.65rem 1rem; font-weight: 600; font-size: 0.72rem; color: var(--cf-ink); background: var(--cf-surface-alt); border-bottom: 1px solid var(--cf-border); white-space: nowrap; letter-spacing: 0.02em; text-align: left; }
.th--right { text-align: right; }
.table-row { border-bottom: 1px solid var(--cf-surface-alt); }
.table-row:hover { background: var(--cf-surface); }
.td { padding: 0.6rem 1rem; font-size: 0.82rem; color: var(--cf-ink); vertical-align: middle; white-space: nowrap; font-variant-numeric: tabular-nums; }
.td--right { text-align: right; }
.tc-foot .td { font-weight: 700; color: var(--cf-ink-strong); background: var(--cf-surface); }
.tc-cname { font-weight: 600; color: var(--cf-ink-strong); }
.tc-cmeta { font-size: 0.72rem; color: var(--cf-muted); }
.cf-iconbtn { height: 2rem; width: 2rem; display: inline-flex; align-items: center; justify-content: center; border: none; border-radius: var(--cf-radius-sm); cursor: pointer; font-size: 1rem; background: var(--cf-surface-alt); color: var(--cf-muted); margin-left: 0.25rem; }
.cf-iconbtn:hover { background: rgb(var(--primary) / 0.12); color: rgb(var(--primary)); }
.tc-badge--ok { background: #ecfdf5; color: #059669; }
.tc-badge--off { background: var(--cf-surface-alt); color: var(--cf-muted); }
.tc-badge--bad { background: #fef2f2; color: #dc2626; }

/* banques / écarts */
.tc-bank { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; padding: 0.5rem 0; border-top: 1px solid var(--cf-border); font-size: 0.82rem; }
.tc-bank:first-child { border-top: 0; }
.tc-bank b { font-variant-numeric: tabular-nums; white-space: nowrap; }

/* squelettes */
.tc-skel { height: 0.75rem; border-radius: 6px; margin: 0.55rem 0; background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%); background-size: 200% 100%; animation: tc-sh 1.3s infinite; }
.tc-skel--lg { height: 1.4rem; }
.tc-skel--chart { height: 260px; }
@keyframes tc-sh { to { background-position: -200% 0; } }

/* mobile : tableau des caisses en cartes */
.tc-only-mobile { display: none; }
.tc-mcards { gap: 0.6rem; padding: 0.75rem; }
.tc-mcard { border: 1px solid var(--cf-border); border-radius: var(--cf-radius-sm); padding: 0.75rem; }
.tc-mcard__r1 { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem; }
.tc-mcard__r2 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.4rem; margin-top: 0.6rem; font-size: 0.72rem; color: var(--cf-muted); }
.tc-mcard__r2 b { display: block; color: var(--cf-ink-strong); font-size: 0.8rem; font-variant-numeric: tabular-nums; }
.tc-mcard__r3 { display: flex; justify-content: flex-end; margin-top: 0.5rem; }
@media (max-width: 700px) {
    .tc-only-desktop { display: none; }
    .tc-only-mobile { display: grid; }
    .tc-kpis { grid-template-columns: minmax(0, 1fr); }
    .tc-kpi__val { font-size: 1.25rem; }
    .tc-alert { flex-wrap: wrap; }
}

</style>
